import { useEffect, useRef } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingResponse } from "@repo/api-client";
import { useVenue } from "../shell/VenueContext";
import { bookingClock, isNum } from "./format";
import { StatusBadge } from "./Badges";

/**
 * The canonical stale-write conflict modal (DOMAIN.md §B.5/§D.3, BK-5). A 409 whose problem carries
 * the authoritative `current` record renders here as a field diff: the losing editor either RELOADS
 * the current record (discard my attempt) or RE-CONFIRMS against it (rebase the CAS `version` and
 * resubmit). It NEVER last-write-wins and never auto-merges — a colleague's capacity-relevant change
 * is always shown first. Terminal current states (a just-cancelled booking) are surfaced too, so the
 * editor sees it can only recreate. Purely presentational; the screen injects the callbacks.
 */
export interface BookingConflictModalProps {
  /** The attempted values (what the user tried to save), for the diff — optional. */
  attempted?: { startSec?: number; partySize?: number };
  current: StaffBookingResponse;
  onReload: () => void;
  onReconfirm: () => void;
}

export function BookingConflictModal({
  attempted,
  current,
  onReload,
  onReconfirm,
}: BookingConflictModalProps) {
  const { t, i18n } = useTranslation();
  const { venue } = useVenue();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  const tz = venue.timeZone;
  const locale = i18n.language;
  const rows: Array<{ label: string; mine?: string; theirs: string }> = [
    {
      label: t("bookings.field.time"),
      mine: isNum(attempted?.startSec) ? bookingClock(attempted.startSec, tz, locale) : undefined,
      theirs: isNum(current.startSec) ? bookingClock(current.startSec, tz, locale) : "—",
    },
    {
      label: t("bookings.field.party"),
      mine: isNum(attempted?.partySize) ? String(attempted.partySize) : undefined,
      theirs: isNum(current.partySize) ? String(current.partySize) : "—",
    },
  ];

  return (
    <div className={overlay}>
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-label={t("bookings.conflict.title")}
        tabIndex={-1}
        className={dialog}
      >
        <h2 className={css({ fontSize: "lg", fontWeight: "bold" })}>
          {t("bookings.conflict.title")}
        </h2>
        <p className={css({ fontSize: "sm" })}>{t("bookings.conflict.message")}</p>

        <div
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "2",
            fontSize: "sm",
            color: "fg.muted",
          })}
        >
          <span>{t("bookings.conflict.currentState")}</span>
          <StatusBadge status={current.status} />
          <span>{t("bookings.conflict.version", { version: current.version ?? 0 })}</span>
        </div>

        <table className={css({ fontSize: "sm", width: "full", borderCollapse: "collapse" })}>
          <thead>
            <tr className={css({ textAlign: "left", color: "fg.muted" })}>
              <th className={cell}>{t("bookings.conflict.field")}</th>
              <th className={cell}>{t("bookings.conflict.mine")}</th>
              <th className={cell}>{t("bookings.conflict.theirs")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className={cell}>{r.label}</td>
                <td className={cell}>{r.mine ?? "—"}</td>
                <td className={`${cell} ${css({ fontWeight: "semibold" })}`}>{r.theirs}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={actions}>
          <button type="button" onClick={onReload} className={secondaryBtn}>
            {t("bookings.conflict.reload")}
          </button>
          <button type="button" onClick={onReconfirm} className={primaryBtn}>
            {t("bookings.conflict.reconfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = css({
  position: "fixed",
  inset: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bg: "black/40",
  zIndex: "50",
  p: "4",
});

const dialog = css({
  bg: "bg.default",
  rounded: "lg",
  borderWidth: "1px",
  p: "5",
  maxW: "lg",
  width: "full",
  display: "flex",
  flexDirection: "column",
  gap: "3",
  outline: "none",
});

const cell = css({ py: "1", pr: "3", verticalAlign: "top" });
const actions = css({ display: "flex", gap: "2", flexWrap: "wrap", justifyContent: "flex-end" });
const secondaryBtn = css({
  px: "3",
  py: "2",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "sm",
});
const primaryBtn = css({
  px: "3",
  py: "2",
  rounded: "md",
  fontSize: "sm",
  bg: "colorPalette.default",
  color: "colorPalette.fg",
});
